import React, { useState, useCallback, useEffect } from 'react';
import { GoogleMap, useLoadScript, Marker } from '@react-google-maps/api';
import './MapPicker.css';

const libraries = ['places'];

let sharedLocationPromise = null;
let sharedLocationResult = null;

const DEFAULT_LOCATION = { lat: 51.5074, lng: -0.1278 }; // London

const MapPicker = ({
  initialLocation,
  onLocationSelect,
  readOnly = false,
  height = '400px'
}) => {
  const [locationReady, setLocationReady] = useState(false);
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
    preventGoogleFontsLoading: true,
  });

  const [selectedPosition, setSelectedPosition] = useState(
    initialLocation || null // Start with null if no initial location
  );
  const [mapCenter, setMapCenter] = useState(
    initialLocation || DEFAULT_LOCATION
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [mapRef, setMapRef] = useState(null);
  const [mapReady, setMapReady] = useState(false);

  // Update position when initialLocation changes (e.g., when league data loads)
  useEffect(() => {
    if (initialLocation) {
      setSelectedPosition(initialLocation);
      setMapCenter(initialLocation);
    }
  }, [initialLocation]);

  // Get user's current location on mount (only if no initial location)
  useEffect(() => {
    if (!initialLocation && !readOnly && typeof window !== 'undefined' && navigator.geolocation) {
      if (sharedLocationResult) {
        setMapCenter(sharedLocationResult);
        setLocationReady(true);
        return;
      }

      if (!sharedLocationPromise) {
        sharedLocationPromise = new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const pos = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              };
              sharedLocationResult = pos;
              resolve(pos);
            },
            (error) => {
              reject(error);
            },
            {
              enableHighAccuracy: true,
              timeout: 5000,
              maximumAge: 0,
            }
          );
        }).catch((error) => {
          sharedLocationPromise = null;
          throw error;
        });
      }

      setLoadingLocation(true);

      sharedLocationPromise
        .then((userPosition) => {
          setMapCenter(userPosition);
          setLocationReady(true);
          setLoadingLocation(false);
        })
        .catch((error) => {
          console.warn('Geolocation error:', error.message);
          setLocationReady(true);
          setLoadingLocation(false);
        });
    } else {
      if (!initialLocation) {
        setMapCenter(DEFAULT_LOCATION);
      }
      setLocationReady(true);
    }
  }, [initialLocation, readOnly]);

  const mapContainerStyle = {
    width: '100%',
    height: height,
  };

  const handleCenterPin = useCallback(() => {
    if (readOnly || !mapRef) return;
    
    // Get the current center of the map
    const center = mapRef.getCenter();
    const newPosition = {
      lat: center.lat(),
      lng: center.lng(),
    };
    setSelectedPosition(newPosition);
    onLocationSelect?.(newPosition);
  }, [readOnly, mapRef, onLocationSelect]);

  const onMapLoad = useCallback((map) => {
    setMapRef(map);
    // Give the map a moment to fully initialize before rendering markers
    setTimeout(() => {
      setMapReady(true);
    }, 500);
  }, []);

  const handleMarkerDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleMarkerDragEnd = useCallback((event) => {
    if (readOnly) return;
    
    setIsDragging(false);
    const newPosition = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng(),
    };
    setSelectedPosition(newPosition);
    onLocationSelect?.(newPosition);
  }, [readOnly, onLocationSelect]);

  // Custom marker icon - only create when Google Maps is loaded
  const getMarkerIcon = useCallback(() => {
    if (!window.google?.maps) return null;
    
    return {
      path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
      fillColor: isDragging ? '#1d4ed8' : '#2563eb', // Primary blue color (same as btn-primary)
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 2,
      scale: 2.5,
      anchor: new window.google.maps.Point(12, 22),
    };
  }, [isDragging]);

  const handleSearch = useCallback(() => {
    if (!searchQuery.trim() || readOnly || !isLoaded || !window.google?.maps) return;

    // Use Geocoding to resolve the search text into coordinates
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: searchQuery }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        const newPosition = {
          lat: location.lat(),
          lng: location.lng(),
        };
        setMapCenter(newPosition);
        // Pan the map to the new location
        if (mapRef) {
          mapRef.panTo(newPosition);
          mapRef.setZoom(15);
        }
      } else {
        alert('Location not found. Please try a different search.');
      }
    });
  }, [searchQuery, readOnly, isLoaded, mapRef]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  if (loadError) {
    return (
      <div className="map-error">
        <p>❌ Error loading Google Maps. Please check your API key.</p>
      </div>
    );
  }

  if (!isLoaded || !locationReady) {
    return (
      <div className="map-loading">
        🗺️ Loading map...
      </div>
    );
  }

  return (
    <div className="map-picker">
      {loadingLocation && (
        <div className="map-loading">
          📍 Detecting your location...
        </div>
      )}
      
      {!readOnly && (
        <div className="map-search">
          <input
            type="text"
            className="form-input"
            placeholder="Search for a location (e.g., New York, NY)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <button
            type="button"
            onClick={handleSearch}
            className="btn btn-secondary"
          >
            Search
          </button>
        </div>
      )}

      {!readOnly && (
        <div className="map-hints">
          <div className="map-hint-item">🗺️ Pan and zoom to find location</div>
          <div className="map-hint-item">🤏 Drag the pin to fine-tune position</div>
          <button 
            type="button"
            onClick={handleCenterPin}
            className="center-pin-button"
          >
            📍 Click here to place pin
          </button>
        </div>
      )}

      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={mapCenter}
        zoom={readOnly ? 15 : 12}
        onLoad={onMapLoad}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          gestureHandling: 'greedy',
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'on' }]
            }
          ]
        }}
      >
        {mapReady && selectedPosition && selectedPosition.lat && selectedPosition.lng && (
          <Marker
            position={{ lat: selectedPosition.lat, lng: selectedPosition.lng }}
            draggable={!readOnly}
            onDragStart={handleMarkerDragStart}
            onDragEnd={handleMarkerDragEnd}
            animation={!readOnly && !isDragging && window.google?.maps ? window.google.maps.Animation.BOUNCE : null}
            icon={getMarkerIcon()}
          />
        )}
      </GoogleMap>

      {!readOnly && isDragging && (
        <div className="map-instructions">
          <p className="dragging-hint">
            ✨ Dragging pin... Release to set location
          </p>
        </div>
      )}
    </div>
  );
};

export default MapPicker;

